import { create } from "zustand";
import { ApiError, api } from "@/lib/api";
import { outboxJobs, removeOutboxJob } from "@/lib/offline";

interface SyncState {
  state: "synced" | "pending" | "syncing" | "offline" | "error";
  pending: number;
  flush: () => Promise<void>;
  refresh: () => Promise<void>;
}

let activeFlush: Promise<void> | null = null;

export const useSyncStore = create<SyncState>((set) => ({
  state: navigator.onLine ? "synced" : "offline",
  pending: 0,
  async refresh() {
    const jobs = await outboxJobs().catch(() => []);
    set({ pending: jobs.length, state: navigator.onLine ? (jobs.length ? "pending" : "synced") : "offline" });
  },
  async flush() {
    if (activeFlush) return activeFlush;
    activeFlush = (async () => {
      if (!navigator.onLine) {
        const jobs = await outboxJobs().catch(() => []);
        set({ state: "offline", pending: jobs.length });
        return;
      }
      const jobs = await outboxJobs();
      if (!jobs.length) {
        set({ state: "synced", pending: 0 });
        return;
      }
      set({ state: "syncing", pending: jobs.length });
      for (const job of jobs) {
        try {
          if (job.kind === "session_answer") await api.session.answer(job.profileId, job.body);
          if (job.kind === "session_complete") await api.session.complete(job.profileId, job.body.session_id);
          if (job.kind === "relay_answer") await api.relay.answer(job.profileId, job.body);
          if (job.kind === "relay_complete") await api.relay.complete(job.profileId, job.body.date_key);
          await removeOutboxJob(job.id);
        } catch (error) {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 401) {
            await removeOutboxJob(job.id);
            continue;
          }
          set({ state: navigator.onLine ? "error" : "offline" });
          break;
        }
      }
      const remaining = await outboxJobs();
      set({ pending: remaining.length, state: remaining.length ? (navigator.onLine ? "pending" : "offline") : "synced" });
    })().finally(() => { activeFlush = null; });
    return activeFlush;
  },
}));

export async function queueChanged(): Promise<void> {
  await useSyncStore.getState().refresh();
  void useSyncStore.getState().flush();
}
